import { gql } from "utils/urql";
import Head from "next/head";
import { ArticleMetadataFragment } from "types/api";
import { defaultDek, defaultMetaImage } from "sharedConstants";

const ArticleMetadata = ({
  title,
  seoTitle,
  shareTitle,
  dek,
  shareDek,
  url,
  canonicalUrl,
  authors,
  shareImage2x1,
  ampUrl,
  fbiaUrl,
  hasMeter,
  channels,
  primaryChannel,
  datePublished,
  dateModified,
  watsonInfo,
}: ArticleMetadataFragment) => {
  const isOpinionArticle = false;
  let joinedAuthorNames = "";

  return (
    <Head>
      <meta name="description" key="description" content={dek || defaultDek} />
      <meta
        property="krux:title"
        key=""
        content={`${seoTitle || title} - The Atlantic`}
      />
      <meta
        property="krux:description"
        key="krux:description"
        content={dek || defaultDek}
      />
      <link rel="canonical" key="canonical" href={canonicalUrl || ""} />
      <link
        rel="image_src"
        key="image_src"
        href={shareImage2x1?.url || defaultMetaImage}
      />

      {authors &&
        authors.map((author, i) => {
          joinedAuthorNames +=
            author.displayName + (i === authors.length - 1 ? "" : ", ");
          return author.socialMedia.map((social) => {
            return (
              social.platform === "FACEBOOK" && (
                <meta
                  property="article:author"
                  key="article:author"
                  content={`https://www.facebook.com/${social.url}`}
                />
              )
            );
          });
        })}

      {authors && (
        <meta name="author" key="author" content={joinedAuthorNames} />
      )}

      {ampUrl && <link rel="amphtml" key="amphtml" href={ampUrl} />}
      {fbiaUrl && (
        <link rel="ia:markup_url" key="ia:markup_url" href={fbiaUrl} />
      )}
      <meta
        property="article:publisher"
        key="article:publisher"
        content="https://www.facebook.com/TheAtlantic/"
      />
      <meta
        property="article:opinion"
        key="article:opinion"
        content={isOpinionArticle ? "true" : "false"}
      />
      <meta
        property="article:content_tier"
        key="article:content_tier"
        content={hasMeter ? "metered" : "free"}
      />
      {channels.map((channel) => {
        return (
          <meta
            property="article:tag"
            key="article:tag"
            content={channel.slug}
          />
        );
      })}
      <meta
        property="article:section"
        key="article:section"
        content={primaryChannel?.displayName}
      />
      <meta
        property="article:published_time"
        key="article:published_time"
        content={datePublished || ""}
      />
      <meta
        property="article:modified_time"
        key="article:modified_time"
        content={dateModified || ""}
      />

      {datePublished ? (
        <meta name="ROBOTS" key="ROBOTS" content="INDEX, FOLLOW" />
      ) : (
        <meta name="ROBOTS" key="ROBOTS" content="NOINDEX, NOFOLLOW" />
      )}
      {/* Article-specific og: meta tags */}
      <meta property="og:title" content={shareTitle || title} key="og:title" />
      <meta
        property="og:description"
        content={shareDek || dek || ""}
        key="og:description"
      />
      <meta property="og:url" content={url} key="og:url" />
      <meta property="og:type" content="article" key="og:type" />
      <meta
        property="og:image"
        content={shareImage2x1?.url || ""}
        key="og:image"
      />
      {/* Article-specific twitter meta tags */}
      <meta
        property="twitter:card"
        content="summary_large_image"
        key="twitter:card"
      />
      {/* feeds - for all articles */}
      <link
        rel="alternate"
        type="application/rss+xml"
        title="The Atlantic"
        href="/feed/all/"
        key="feed-all"
      />
      <link
        rel="alternate"
        type="application/rss+xml"
        title="Best of The Atlantic"
        href="/feed/best-of/"
        key="feed-best-of"
      />
      {/* HTTPS referrer policy */}
      <meta name="referrer" content="unsafe-url" key="referrer" />
      {/* Apple web app tags */}
      <meta
        name="apple-mobile-web-app-capable"
        content="yes"
        key="apple-mobile-web-app-capable"
      />
      <meta
        name="apple-mobile-web-status-bar-style"
        key="apple-mobile-web-status-bar-style"
        content="black"
      />
      <meta
        name="apple-mobile-web-app-title"
        key="apple-mobile-web-app-title"
        content="The Atlantic"
      />
      <meta
        key="keywords"
        name="keywords"
        content={watsonInfo?.keywords.join(", ")}
        itemID="#keywords"
      />
      <meta
        key="news_keywords"
        name="news_keywords"
        content={watsonInfo?.keywords.join(", ")}
      />
      <link
        key="AGaramondPro"
        rel="preload"
        as="font"
        type="font/woff2"
        href="https://www.theatlantic.com/packages/fonts/garamond/AGaramondPro-Regular.woff2"
        crossOrigin=""
      />
      <link
        key="Graphik"
        rel="preload"
        as="font"
        type="font/woff2"
        href="https://www.theatlantic.com/packages/fonts/graphik/Graphik-Regular-Web.woff2"
        crossOrigin=""
      />
      <link
        key="GraphikSemibold"
        rel="preload"
        as="font"
        type="font/woff2"
        href="https://www.theatlantic.com/packages/fonts/graphik/Graphik-Semibold-Web.woff2"
        crossOrigin=""
      />
    </Head>
  );
};

export default ArticleMetadata;

ArticleMetadata.fragment = gql`
  fragment articleMetadata on Article {
    title
    seoTitle
    shareTitle
    dek
    shareDek
    url
    ampUrl
    fbiaUrl
    canonicalUrl
    hasMeter
    datePublished
    dateModified
    shareImage2x1: shareImage(width: 960, height: 500) {
      url
      height
      width
    }
    primaryChannel {
      displayName
    }
    authors {
      displayName
      socialMedia {
        platform
        url
      }
    }
    channels {
      slug
    }
    watsonInfo {
      keywords
    }
  }
`;
